import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

// CHARTS & RECHARTS COMPONENTS
// ICONS
// API SERVICES
import { getAllStats } from "../../../../api/report/thirtyDaysStatisticsReport";
import { BenefitsStats } from "./BenefitsStats";
import { CancelledOrdersStats } from "./CancelledOrdersStats";
import HourlyDistributionChart from "../charts/HourlyDistributionChart";
import { IndoorStats } from "./IndoorStats";
import { SalesTrendChart } from "./SalesTrendChart";
import { TakeoutStats } from "./TakeoutStats";
import { ComparisonChart } from "./ComparisonChart";
import DeliveryStats from "./DeliveryStats";
import { ChannelDailySalesChart } from "../charts/ChannelDailySalesChart";
import { MerchantDailySalesChart } from "../charts/MerchantDailySalesChart";

// CUSTOM COMPONENTS

// ANIMATION VARIANTS
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

// MAIN COMPONENT
export default function RestaurantStatsView({ startDate, endDate }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    salesData: [],
    indoorData: null,
    deliveryData: null,
    takeoutData: null,
    comparisonData: [],
    hourlyData: [],
    benefitsData: [],
    cancelledData: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Opção 1: Buscar todos os dados de um endpoint combinado
        const response = await getAllStats();
        setData(response.data);
        setLoading(false);

        /*
                // Opção 2: Buscar dados em paralelo de endpoints individuais para melhor desempenho
                const [
                  salesResponse,
                  indoorResponse,
                  deliveryResponse,
                  takeoutResponse,
                  comparisonResponse,
                  hourlyResponse,
                  benefitsResponse,
                  cancelledResponse
                ] = await Promise.all([
                  axios.get('/api/restaurant-stats/sales/'),
                  axios.get('/api/restaurant-stats/indoor/'),
                  axios.get('/api/restaurant-stats/delivery/'),
                  axios.get('/api/restaurant-stats/takeout/'),
                  axios.get('/api/restaurant-stats/comparison/'),
                  axios.get('/api/restaurant-stats/hourly/'),
                  axios.get('/api/restaurant-stats/benefits/'),
                  axios.get('/api/restaurant-stats/cancelled/')
                ]);

                setData({
                  salesData: salesResponse.data,
                  indoorData: indoorResponse.data,
                  deliveryData: deliveryResponse.data,
                  takeoutData: takeoutResponse.data,
                  comparisonData: comparisonResponse.data,
                  hourlyData: hourlyResponse.data,
                  benefitsData: benefitsResponse.data,
                  cancelledData: cancelledResponse.data
                });
                setLoading(false);
                */
      } catch (error) {
        console.error("Error fetching restaurant stats:", error);
        setError(
          "Failed to load restaurant statistics. Please try again later."
        );
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Performance Da Operação (Últimos 30 Dias)
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Dados compreensíveis sobre as vendas da sua operação
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Sales Trend Chart */}
          <Grid item xs={12} component={motion.div} variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <SalesTrendChart data={data.salesData} loading={loading} />
            </Paper>
          </Grid>

          <Grid item xs={12} component={motion.div} variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <MerchantDailySalesChart />
            </Paper>
          </Grid>

          <Grid item xs={12} component={motion.div} variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <ChannelDailySalesChart />
            </Paper>
          </Grid>

          {/* Delivery Stats */}
          <Grid
            item
            xs={12}
            md={4}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              {data.deliveryData ? (
                <DeliveryStats data={data.deliveryData} loading={loading} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Indoor Stats */}
          <Grid
            item
            xs={12}
            md={4}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              {data.indoorData ? (
                <IndoorStats data={data.indoorData} loading={loading} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Takeout Stats */}
          <Grid
            item
            xs={12}
            md={4}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              {data.takeoutData ? (
                <TakeoutStats data={data.takeoutData} loading={loading} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Month-over-Month Comparison */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <ComparisonChart data={data.comparisonData} loading={loading} />
            </Paper>
          </Grid>

          {/* Hourly Distribution */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <HourlyDistributionChart
                data={data.hourlyData}
                loading={loading}
              />
            </Paper>
          </Grid>

          {/* Novos componentes: Benefícios */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <BenefitsStats data={data.benefitsData} loading={loading} />
            </Paper>
          </Grid>

          {/* Novos componentes: Cancelamentos */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CancelledOrdersStats
                data={data.cancelledData}
                loading={loading}
              />
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
}
